import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { createFinContainer, type FinContainer } from '@lemontaigu/fin-adapters';
import { usePmsScope } from './PmsProvider.js';
import { supabase } from './supabase.js';

// The C6 Finance composition root for the PMS app (docs/PMS-LEDGER-UI-BRIEF.md §6). The ONE place the
// Supabase client is bound to the finance ports: the screens below call `useFin()` and depend on the
// fin-core `LedgerReader` interface, never on the client. Shared by pms.* and the bos.* mount
// (source-exported, one definition), like PmsProvider.

/** The book the screen reads. `PROOF` is the rehearsal book; the SAS book opens on 2026-12-01 and this
 *  constant is the one line that changes then (together with the « livre d'essai » notice, which
 *  follows the book's code and disappears by itself). */
export const ACTIVE_BOOK = 'PROOF';

export interface FinServices extends FinContainer {
  readonly book: string;
}

const FinContext = createContext<FinServices | null>(null);

/** Must sit inside <PmsProvider>: the reader is bound to the établissement the operator is looking at.
 *  The PMS provider remounts its children when that choice changes, so this rebuilds with it. */
export function FinProvider({ children }: { children: ReactNode }) {
  const { current } = usePmsScope();
  const services = useMemo<FinServices | null>(
    () => (current ? { ...createFinContainer(supabase, current), book: ACTIVE_BOOK } : null),
    [current],
  );
  return <FinContext.Provider value={services}>{children}</FinContext.Provider>;
}

/** null when no établissement is resolved yet: the screen says so rather than guessing one. */
export function useFin(): FinServices | null {
  return useContext(FinContext);
}
